import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import FilePlayer from 'react-player/file';
import { useTranslation } from 'react-i18next';

import { stripTags } from '@klv/shared/utils/functions';
import { useEagerLoadingContext } from '@klv/context/eager-loading';

import * as s from './styles';

import type { TDecorativeVideo } from './types';


const DecorativeVideo = ({
  caption = '', loop = false, poster, posterImageData, title = '', url, ...props
}: TDecorativeVideo) => {
  const { t } = useTranslation();

  const eagerLoad = useEagerLoadingContext();
  
  const [hasEverEnteredView, setHasEverEnteredView] = useState(false);
  const { ref, inView } = useInView({ threshold: 0.25 });

  if (inView && !hasEverEnteredView) {
    setHasEverEnteredView(true);
  }

  const captionText = caption && stripTags(caption);
  let ariaLabel = title;

  if (ariaLabel && captionText) {
    ariaLabel += `: ${captionText}`;
  } else if (captionText) {
    ariaLabel = captionText;
  }

  ariaLabel = ariaLabel && `(Video) ${ariaLabel.trim()}`;

  if (url) {
    return (
      <s.VideoWrapper ref={ref}>
        <s.Poster alt="" image={posterImageData} loading={eagerLoad}/>
        {hasEverEnteredView && (
            <s.StyledVimeoPlayer
              aria-label={ariaLabel}
              config={{
                vimeo: {
                  playerOptions: {
                    autopause: false,
                    background: true,
                    keyboard: false,
                    loop,
                    responsive: true,
                    texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage'),        
                  },
                },
              }}
              playing={inView}
              url={url}
              height="100%"
              width="100%"
              muted
              volume={0}
              playsinline
              {...props}
            />
        )}
      </s.VideoWrapper>
    );
  }

  return (
    <s.VideoWrapper ref={ref}>
      {hasEverEnteredView && (
          <FilePlayer
            aria-label={ariaLabel}
            config={poster ? { attributes: { poster } } : {}}
            loop={loop}
            muted
            playing={inView}
            playsinline
            height="100%"
            width="100%"
            volume={0}
            {...props}
          />
      )}
    </s.VideoWrapper>
  );
};

export default DecorativeVideo;
